/**
 * UI strings for collections + daily habit (col.* hab.*): creature book, sticker album, gallery wall,
 * Today's drawing card, weekly garden, drawing calendar and reminder preview. `vi` covers every key.
 */
export const en = {
  'col.title': 'My collection',
  'col.rail': 'Collect',
  'col.tab.creatures': 'Creatures',
  'col.tab.stickers': 'Stickers',
  'col.tab.gallery': 'Gallery',
  'col.tab.calendar': 'Calendar',
  'col.level': 'Level {n}',
  'col.book.title': 'Creature book',
  'col.book.found': '{n} of {total} found',
  'col.book.next': 'Next to discover',
  'col.book.nextBody': 'Draw it to add its card to your book!',
  'col.book.mystery': 'Mystery',
  'col.book.drawn': 'Drawn {n}×',
  'col.book.firstFound': 'Found on {date}',
  'col.book.yours': 'Your drawing',
  'col.book.juno': "Juno's drawing",
  'col.book.drawAgain': 'Draw it again',
  'col.book.drawIt': 'Draw it to discover',
  'col.book.new': 'New!',
  'col.book.noPhoto': 'Snap a photo next time to see your drawing here',
  'col.book.worldDone': 'World complete!',
  'col.rarity.common': 'Common',
  'col.rarity.special': 'Special',
  'col.rarity.rare': 'Rare',
  'col.rarity.legendary': 'Legendary',
  'col.album.title': 'Sticker album',
  'col.album.tray': 'New stickers',
  'col.album.trayHint': 'Tap a sticker to peel it off and stick it in!',
  'col.album.trayEmpty': 'Draw more to earn new stickers',
  'col.album.placeAll': 'Stick them all',
  'col.album.friends': 'Friends',
  'col.album.count': '{n} of {total} stuck in',
  'col.album.drawToEarn': 'Draw {title} to earn this sticker',
  'col.album.stuck': 'Stuck in!',
  'col.req.free': 'Ready to use',
  'col.req.drawings': 'Make {n} drawings',
  'col.req.level': 'Reach level {n}',
  'col.req.blooms': 'Grow {n} weekly flowers',
  'col.req.creatures': 'Find {n} creatures',
  'col.gallery.title': 'Gallery wall',
  'col.gallery.hint': 'Tap a drawing to pick its frame',
  'col.gallery.empty': 'Your drawings will hang here. Draw one to start your gallery!',
  'col.gallery.frames': 'Frames',
  'col.gallery.hang': 'Hang in Studio',
  'col.gallery.hung': 'In My Studio',
  'col.gallery.unlocked': '{n} of {total} frames unlocked',
  'col.gallery.locked': 'Locked frame',
  'col.frame.wood': 'Oak',
  'col.frame.cream': 'Cream mat',
  'col.frame.crayon': 'Crayon',
  'col.frame.pastel': 'Candy',
  'col.frame.gold': 'Gold',
  'col.frame.starry': 'Starry night',
  'col.frame.rainbow': 'Rainbow',
  'hab.today': "Today's drawing",
  'hab.draw': 'Draw now',
  'hab.minutes': 'About {n} min',
  'hab.reason.favourite': 'Because you love {cat}',
  'hab.reason.stretch': 'A little challenge for you',
  'hab.reason.fresh': 'Something new to try',
  'hab.reason.again': 'An old friend, drawn even better',
  'hab.doneTitle': 'You drew {title} today!',
  'hab.doneBody': 'A new surprise is waiting tomorrow.',
  'hab.drawMore': 'Draw something else',
  'hab.week': '{n} of {goal} this week',
  'hab.plant.0': 'Draw to plant a seed',
  'hab.plant.1': 'A sprout! Keep going',
  'hab.plant.2': 'A bud! Almost there',
  'hab.plant.3': 'Your flower is blooming',
  'hab.garden': 'My garden',
  'hab.gardenBody': 'Each week you reach your goal, a flower stays in your garden for ever.',
  'hab.blooms': '{n} flowers',
  'hab.bloomOne': '1 flower',
  'hab.thisWeek': 'This week',
  'hab.goal': 'Weekly goal',
  'hab.goalN': '{n} a week',
  'hab.goalHint': 'For grown-ups: a gentle rhythm, not a streak. Missing a week never takes anything away.',
  'hab.calendar': 'Drawing calendar',
  'hab.todayChip': 'Today',
  'hab.noDrawings': 'No drawings on this day',
  'hab.drawingsOn': 'Drawings on {date}',
  'hab.monthCount': '{n} drawings this month',
  'hab.reminder': 'Next reminder',
  'hab.reminderOff': 'Reminders are off. A grown-up can turn them on in Settings.',
  'hab.reminderOn': '{days} at {time}',
  'hab.openSettings': 'Settings',
  'hab.weekdays': 'Mon,Tue,Wed,Thu,Fri,Sat,Sun',
  'hab.prev': 'Previous month',
  'hab.nextMonth': 'Next month',
};

export const vi: Record<keyof typeof en, string> = {
  'col.title': 'Bộ sưu tập',
  'col.rail': 'Sưu tập',
  'col.tab.creatures': 'Sinh vật',
  'col.tab.stickers': 'Sticker',
  'col.tab.gallery': 'Phòng tranh',
  'col.tab.calendar': 'Lịch vẽ',
  'col.level': 'Cấp {n}',
  'col.book.title': 'Sổ sinh vật',
  'col.book.found': 'Đã tìm thấy {n}/{total}',
  'col.book.next': 'Sắp khám phá',
  'col.book.nextBody': 'Vẽ bạn ấy để thêm thẻ vào sổ của con nhé!',
  'col.book.mystery': 'Bí ẩn',
  'col.book.drawn': 'Đã vẽ {n} lần',
  'col.book.firstFound': 'Tìm thấy ngày {date}',
  'col.book.yours': 'Tranh của con',
  'col.book.juno': 'Tranh của cô Juno',
  'col.book.drawAgain': 'Vẽ lại lần nữa',
  'col.book.drawIt': 'Vẽ để khám phá',
  'col.book.new': 'Mới!',
  'col.book.noPhoto': 'Lần sau chụp ảnh tranh để thấy tranh của con ở đây nhé',
  'col.book.worldDone': 'Hoàn thành thế giới!',
  'col.rarity.common': 'Thường',
  'col.rarity.special': 'Đặc biệt',
  'col.rarity.rare': 'Hiếm',
  'col.rarity.legendary': 'Huyền thoại',
  'col.album.title': 'Album sticker',
  'col.album.tray': 'Sticker mới',
  'col.album.trayHint': 'Chạm vào sticker để bóc ra và dán vào album nhé!',
  'col.album.trayEmpty': 'Vẽ thêm để nhận sticker mới',
  'col.album.placeAll': 'Dán hết',
  'col.album.friends': 'Những người bạn',
  'col.album.count': 'Đã dán {n}/{total}',
  'col.album.drawToEarn': 'Vẽ {title} để nhận sticker này',
  'col.album.stuck': 'Dán xong!',
  'col.req.free': 'Dùng được ngay',
  'col.req.drawings': 'Vẽ {n} bức tranh',
  'col.req.level': 'Đạt cấp {n}',
  'col.req.blooms': 'Trồng {n} bông hoa tuần',
  'col.req.creatures': 'Tìm {n} sinh vật',
  'col.gallery.title': 'Tường tranh',
  'col.gallery.hint': 'Chạm vào tranh để chọn khung',
  'col.gallery.empty': 'Tranh của con sẽ được treo ở đây. Vẽ một tranh để mở phòng tranh nhé!',
  'col.gallery.frames': 'Khung tranh',
  'col.gallery.hang': 'Treo ở Xưởng vẽ',
  'col.gallery.hung': 'Đang ở Xưởng vẽ',
  'col.gallery.unlocked': 'Đã mở {n}/{total} khung',
  'col.gallery.locked': 'Khung chưa mở',
  'col.frame.wood': 'Gỗ sồi',
  'col.frame.cream': 'Viền kem',
  'col.frame.crayon': 'Bút sáp',
  'col.frame.pastel': 'Kẹo ngọt',
  'col.frame.gold': 'Khung vàng',
  'col.frame.starry': 'Đêm đầy sao',
  'col.frame.rainbow': 'Cầu vồng',
  'hab.today': 'Tranh hôm nay',
  'hab.draw': 'Vẽ ngay',
  'hab.minutes': 'Khoảng {n} phút',
  'hab.reason.favourite': 'Vì con thích {cat}',
  'hab.reason.stretch': 'Một thử thách nhỏ cho con',
  'hab.reason.fresh': 'Thử một điều mới nhé',
  'hab.reason.again': 'Bạn cũ, lần này vẽ còn đẹp hơn',
  'hab.doneTitle': 'Hôm nay con đã vẽ {title}!',
  'hab.doneBody': 'Ngày mai lại có bất ngờ mới đang chờ.',
  'hab.drawMore': 'Vẽ thêm tranh khác',
  'hab.week': '{n}/{goal} tranh tuần này',
  'hab.plant.0': 'Vẽ một tranh để gieo hạt',
  'hab.plant.1': 'Mầm nhú rồi! Vẽ tiếp nhé',
  'hab.plant.2': 'Có nụ rồi! Sắp nở',
  'hab.plant.3': 'Hoa của con đã nở',
  'hab.garden': 'Khu vườn của con',
  'hab.gardenBody': 'Mỗi tuần đạt mục tiêu, một bông hoa sẽ ở lại vườn của con mãi mãi.',
  'hab.blooms': '{n} bông hoa',
  'hab.bloomOne': '1 bông hoa',
  'hab.thisWeek': 'Tuần này',
  'hab.goal': 'Mục tiêu tuần',
  'hab.goalN': '{n} tranh/tuần',
  'hab.goalHint': 'Dành cho bố mẹ: một nhịp nhẹ nhàng, không phải chuỗi ngày. Bỏ lỡ một tuần cũng không mất gì cả.',
  'hab.calendar': 'Lịch vẽ tranh',
  'hab.todayChip': 'Hôm nay',
  'hab.noDrawings': 'Ngày này chưa có tranh',
  'hab.drawingsOn': 'Tranh ngày {date}',
  'hab.monthCount': '{n} tranh trong tháng',
  'hab.reminder': 'Lời nhắc tiếp theo',
  'hab.reminderOff': 'Lời nhắc đang tắt. Bố mẹ có thể bật trong Cài đặt.',
  'hab.reminderOn': '{days} lúc {time}',
  'hab.openSettings': 'Cài đặt',
  'hab.weekdays': 'T2,T3,T4,T5,T6,T7,CN',
  'hab.prev': 'Tháng trước',
  'hab.nextMonth': 'Tháng sau',
};
